/* ==========================================================================
   BLISS — After Dark Date-Night Kit opt-in. Block 3.

   Two independent jobs in one file, because they share the same component:

   1. FORM  — if Klaviyo is configured, submit straight to Klaviyo's client
              subscriptions endpoint and then move the visitor on to the
              thank-you page. If it is not configured, or anything fails, get
              out of the way and let Shopify's native customer form post
              normally. A lead is never silently dropped.

   2. POPUP — open on delay or exit intent, once per visitor, dismissible,
              and suppressed for people who are already subscribed and
              anywhere near the checkout.
   ========================================================================== */

(function () {
  'use strict';

  var KLAVIYO_REVISION = '2024-10-15';
  var STORE_KEY = 'bliss:after-dark';

  /* ---------------------------------------------------------------- storage */

  function readSeen() {
    try {
      var raw = window.localStorage.getItem(STORE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeSeen(state) {
    try {
      window.localStorage.setItem(STORE_KEY, JSON.stringify(state));
    } catch (e) {
      /* private mode — the popup simply reverts to once per session in memory */
    }
  }

  var memorySeen = false;

  function suppressedByHistory(days) {
    if (memorySeen) return true;
    var state = readSeen();
    if (!state) return false;
    if (state.subscribed) return true;
    if (!state.at) return false;
    var elapsedDays = (Date.now() - state.at) / 86400000;
    return elapsedDays < (days || 30);
  }

  function remember(extra) {
    memorySeen = true;
    var state = readSeen() || {};
    state.at = Date.now();
    if (extra && extra.subscribed) state.subscribed = true;
    writeSeen(state);
  }

  /* ------------------------------------------------------------------- form */

  function setStatus(root, message, isError) {
    var el = root.querySelector('[data-bliss-status]');
    if (!el) return;
    el.textContent = message || '';
    el.hidden = !message;
    el.classList.toggle('bliss-form__status--error', !!isError);
    if (message) el.setAttribute('role', isError ? 'alert' : 'status');
  }

  function subscribeToKlaviyo(companyId, listId, email, firstName) {
    var body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'BLISS After Dark Date-Night Kit',
          profile: {
            data: {
              type: 'profile',
              attributes: { email: email }
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: listId } }
        }
      }
    };

    if (firstName) body.data.attributes.profile.data.attributes.first_name = firstName;

    return fetch(
      'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId),
      {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          revision: KLAVIYO_REVISION
        },
        body: JSON.stringify(body)
      }
    ).then(function (response) {
      // Klaviyo answers 202 with no body on success.
      if (response.status === 202 || response.ok) return true;
      return response.text().then(function (text) {
        throw new Error(text || 'Klaviyo returned ' + response.status);
      });
    });
  }

  function wireForm(root) {
    var form = root.querySelector('form');
    if (!form || form.dataset.blissWired) return;
    form.dataset.blissWired = 'true';

    var companyId = root.dataset.companyId;
    var listId = root.dataset.listId;
    var successUrl = root.dataset.successUrl;

    form.addEventListener('submit', function (event) {
      var honeypot = form.querySelector('[name="contact[company]"]');
      if (honeypot && honeypot.value) {
        event.preventDefault();
        return;
      }

      var emailField = form.querySelector('[data-bliss-email]');
      var email = emailField ? emailField.value.trim() : '';

      if (!email) {
        event.preventDefault();
        setStatus(root, 'Please enter your email address.', true);
        if (emailField) emailField.focus();
        return;
      }

      // No Klaviyo configured — let Shopify's native form post handle it.
      if (!companyId || !listId) {
        remember({ subscribed: true });
        return;
      }

      event.preventDefault();

      var nameField = form.querySelector('[data-bliss-first-name]');
      var firstName = nameField ? nameField.value.trim() : '';
      var button = form.querySelector('[type="submit"]');

      if (button) button.disabled = true;
      setStatus(root, 'Sending your kit…');

      subscribeToKlaviyo(companyId, listId, email, firstName)
        .then(function () {
          remember({ subscribed: true });
          setStatus(root, 'Thank you. Your kit is on its way.');
          if (successUrl) {
            window.location.href = successUrl;
          } else if (button) {
            button.disabled = false;
          }
        })
        .catch(function () {
          // Klaviyo refused. Fall back to the native Shopify post rather than
          // losing the lead — remove our handler and resubmit.
          if (button) button.disabled = false;
          setStatus(root, '');
          form.dataset.blissWired = '';
          form.submit();
        });
    });
  }

  /* ------------------------------------------------------------------ popup */

  function nearCheckout() {
    var path = window.location.pathname;
    return /\/(cart|checkouts?|orders|account)(\/|$)/.test(path);
  }

  function openPopup(popup) {
    if (!popup.hidden) return;
    popup.dataset.returnFocus = '';
    var active = document.activeElement;
    popup.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    var focusTarget = popup.querySelector('[data-bliss-email]') || popup.querySelector('[data-bliss-close]');
    if (focusTarget) focusTarget.focus();
    popup._restoreFocus = active;
    remember();
  }

  function closePopup(popup) {
    if (popup.hidden) return;
    popup.hidden = true;
    document.documentElement.style.overflow = '';
    if (popup._restoreFocus && popup._restoreFocus.focus) popup._restoreFocus.focus();
    remember();
  }

  function wirePopup(popup) {
    if (popup.dataset.blissWired) return;
    popup.dataset.blissWired = 'true';

    var delay = parseInt(popup.dataset.delay, 10);
    var days = parseInt(popup.dataset.frequencyDays, 10) || 30;
    var useExitIntent = popup.dataset.exitIntent === 'true';
    var isSubscriber = popup.dataset.subscriber === 'true';
    var inEditor = !!(window.Shopify && window.Shopify.designMode);

    popup.querySelectorAll('[data-bliss-close]').forEach(function (button) {
      button.addEventListener('click', function () { closePopup(popup); });
    });

    popup.addEventListener('click', function (event) {
      if (event.target === popup) closePopup(popup);
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closePopup(popup);
    });

    // Keep focus inside the dialog while it is open.
    popup.addEventListener('keydown', function (event) {
      if (event.key !== 'Tab' || popup.hidden) return;
      var focusable = popup.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    // In the theme editor the merchant needs to see it on demand, not on a
    // timer that fights them while they edit.
    if (inEditor) {
      document.addEventListener('shopify:section:select', function (event) {
        if (popup.closest('.shopify-section') === event.target) openPopup(popup);
      });
      document.addEventListener('shopify:section:deselect', function (event) {
        if (popup.closest('.shopify-section') === event.target) closePopup(popup);
      });
      return;
    }

    if (isSubscriber || nearCheckout() || suppressedByHistory(days)) return;

    var armed = true;

    function fire() {
      if (!armed) return;
      armed = false;
      openPopup(popup);
    }

    if (!isNaN(delay) && delay >= 0) {
      setTimeout(fire, delay * 1000);
    }

    if (useExitIntent) {
      document.addEventListener('mouseout', function (event) {
        if (!event.relatedTarget && event.clientY <= 0) fire();
      });
    }
  }

  /* ------------------------------------------------------------------- init */

  function init(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-bliss-optin]').forEach(wireForm);
    scope.querySelectorAll('[data-bliss-popup]').forEach(wirePopup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
